import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as topsApi from '@/api/tourOperators';
import { PreviouslyUsedByNote } from '../lifecycle/PreviouslyUsedByNote';
import { isTenantCode, TENANT_CODE_RULE, tenantCodeInput } from '../tenantCode';
import type { WizardData } from './types';

interface Props {
  data: WizardData;
  logo: File | null;
  onChange: (partial: Partial<WizardData>) => void;
  onLogoChange: (f: File | null) => void;
  /** Called whenever the code availability state changes.
   *  `null` = check in flight or no code entered yet. */
  onCodeAvailability: (available: boolean | null) => void;
}

const DEBOUNCE_MS = 400;

export function Step1BasicInfo({ data, logo, onChange, onLogoChange, onCodeAvailability }: Props) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Debounced code value sent to the query.
  const [debouncedCode, setDebouncedCode] = useState(data.code);

  useEffect(() => {
    if (!logo) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(logo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logo]);

  // Debounce: update debouncedCode 400 ms after the user stops typing.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCode(data.code), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [data.code]);

  // A code that is not a DNS label is refused here; only a well-formed one is asked about.
  const wellFormed = isTenantCode(data.code);
  const codeQuery = useQuery({
    queryKey: ['tourOperators', 'checkCode', debouncedCode],
    queryFn: () => topsApi.checkCode(debouncedCode),
    enabled: isTenantCode(debouncedCode),
    staleTime: 10_000,
    retry: false,
  });

  // Availability: null while debouncing/loading, true/false once resolved.
  const isPending = data.code !== debouncedCode || codeQuery.isFetching;
  const available: boolean | null =
    data.code.length === 0 ? null
    : !wellFormed ? false
    : isPending ? null
    : (codeQuery.data?.available ?? null);

  // A free code another tenant gave up: said, never refused.
  const previousHolders = available === true ? (codeQuery.data?.previously_used_by ?? []) : [];

  // Report up to the dialog on every change.
  useEffect(() => {
    onCodeAvailability(available);
  }, [available, onCodeAvailability]);

  const codeAdornment = data.code.length === 0 ? null
    : isPending
    ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
    : available === true
    ? <InputAdornment position="end"><CheckCircleIcon color="success" fontSize="small" /></InputAdornment>
    : <InputAdornment position="end"><ErrorIcon color="error" fontSize="small" /></InputAdornment>;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <TextField
          label="Name *"
          fullWidth size="small"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Code *"
          fullWidth size="small"
          value={data.code}
          onChange={(e) => onChange({ code: tenantCodeInput(e.target.value) })}
          error={available === false}
          helperText={
            data.code.length > 0 && !wellFormed ? TENANT_CODE_RULE
            : available === false ? (codeQuery.data?.reason ?? 'Code is already taken.')
            : 'Lowercase a-z, 0-9 or -, starting and ending with a letter or digit. Permanent: it is the dashboard address and the MCP URL.'
          }
          InputProps={{ endAdornment: codeAdornment }}
        />
      </Grid>
      {previousHolders.length > 0 && (
        <Grid item xs={12}>
          <PreviouslyUsedByNote holders={previousHolders} />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <TextField
          label="Email"
          fullWidth size="small"
          value={data.email}
          onChange={(e) => onChange({ email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Phone"
          fullWidth size="small"
          value={data.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Website"
          fullWidth size="small"
          value={data.url}
          onChange={(e) => onChange({ url: e.target.value })}
        />
      </Grid>
      {/* The record fields ops fill anyway (11–13 of 13 live tenants) — asked
          here rather than left to the BASIC_PROFILE editor after creation. */}
      <Grid item xs={12} md={8}>
        <TextField
          label="Payment URL"
          fullWidth size="small"
          value={data.payment_url}
          onChange={(e) => onChange({ payment_url: e.target.value })}
          helperText="Where guests pay. Can be overridden per price record."
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.external_payment_processing}
              onChange={(e) => onChange({ external_payment_processing: e.target.checked })}
            />
          }
          label="External payment processing"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Logo *</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => logoInputRef.current?.click()}
          >
            {logo ? 'Change logo' : 'Upload logo'}
          </Button>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              sx={{ height: 48, maxWidth: 120, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              alt="logo preview"
            />
          )}
          {logo && (
            <Typography variant="caption" color="text.secondary">{logo.name}</Typography>
          )}
        </Box>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => onLogoChange(e.target.files?.[0] ?? null)}
        />
      </Grid>
    </Grid>
  );
}
